import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Network, ArrowRight, RefreshCw, ShieldCheck, Building2, Sparkles } from "lucide-react";

const TREE = [
  { id: "r", x: 50, y: 12, c: "#6161FF" },
  { id: "a", x: 22, y: 38, c: "#00C875", p: "r" },
  { id: "b", x: 50, y: 38, c: "#FDAB3D", p: "r" },
  { id: "c", x: 78, y: 38, c: "#A25DDC", p: "r" },
  { id: "d", x: 12, y: 66, c: "#579BFC", p: "a" },
  { id: "e", x: 32, y: 66, c: "#E2445C", p: "a" },
  { id: "f", x: 50, y: 66, c: "#00C875", p: "b" },
  { id: "g", x: 68, y: 66, c: "#6161FF", p: "c" },
  { id: "h", x: 88, y: 66, c: "#FDAB3D", p: "c" },
  { id: "i", x: 24, y: 88, c: "#A25DDC", p: "e" },
  { id: "j", x: 42, y: 88, c: "#579BFC", p: "f" },
  { id: "k", x: 60, y: 88, c: "#00C875", p: "g" },
  { id: "l", x: 78, y: 88, c: "#E2445C", p: "g" },
];

function BrandTree() {
  const map = useMemo(() => Object.fromEntries(TREE.map((n) => [n.id, n])), []);
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {TREE.filter((n) => n.p).map((n, i) => {
        const p = map[n.p];
        return (
          <motion.path key={n.id} d={`M ${p.x} ${p.y + 3} V ${(p.y + n.y) / 2} H ${n.x} V ${n.y - 3}`}
            fill="none" stroke="#6161FF" strokeWidth="0.35" strokeOpacity="0.45"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
            transition={{ duration: 1.2, delay: 0.3 + i * 0.08 }} />
        );
      })}
      {TREE.map((n, i) => (
        <motion.g key={n.id} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: i * 0.09, type: "spring", stiffness: 200 }}>
          <motion.circle cx={n.x} cy={n.y} r="3" fill={n.c}
            animate={{ r: [3, 3.5, 3] }} transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.25 }} />
          <circle cx={n.x} cy={n.y} r="1.1" fill="white" />
        </motion.g>
      ))}
    </svg>
  );
}

const WORDS = ["living", "breathing", "evolving", "connected"];

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [fullName, setFullName] = useState("");
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [trust, setTrust] = useState(true);
  const [wordIdx, setWordIdx] = useState(0);
  const refs = useRef([]);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);
  useEffect(() => {
    const t = setInterval(() => setWordIdx((i) => (i + 1) % WORDS.length), 2600);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestOtp = async (isResend = false) => {
    setBusy(true);
    try {
      const deviceToken = localStorage.getItem("organa_device_token");
      const { data } = await api.post("/auth/request-otp", { email, device_token: deviceToken || null });
      if (data.trusted) {
        login(data.token, data.user);
        toast.success("Welcome back — signed in via trusted device");
        return;
      }
      setDevCode(data.dev_code || null);
      setStep("otp");
      setCooldown(30);
      setDigits(Array(6).fill(""));
      if (!isResend) setTimeout(() => refs.current[0]?.focus(), 150);
      toast.success(data.email_delivered ? "Code sent to your email" : "Code generated (dev mode)");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const signup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", { org_name: orgName, name: fullName, email });
      setDevCode(data.dev_code || null);
      setStep("otp");
      setCooldown(30);
      setDigits(Array(6).fill(""));
      setTimeout(() => refs.current[0]?.focus(), 150);
      toast.success(`Workspace "${orgName}" created — verify your email to enter`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email, code, trust_device: trust,
        device_label: `${navigator.platform || "Device"} · ${/Chrome|Firefox|Safari|Edge/.exec(navigator.userAgent)?.[0] || "Browser"}`,
      });
      if (data.device_token) localStorage.setItem("organa_device_token", data.device_token);
      login(data.token, data.user);
    } catch (e) {
      toast.error(errMsg(e));
      setDigits(Array(6).fill(""));
      refs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const onDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
    if (next.every((d) => d) && next.join("").length === 6) verify(next.join(""));
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setDigits(text.split(""));
      verify(text);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col bg-[#F0F3FF] relative overflow-hidden organa-grain">
        <div className="p-12 z-10">
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-xl bg-[#6161FF] flex items-center justify-center shadow-lg shadow-[#6161FF]/30">
              <Network size={20} className="text-white" />
            </span>
            <span className="font-display font-extrabold text-2xl tracking-tight">Organa</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9699AD] mt-1">by Zefficacy</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-14 text-5xl font-black leading-[1.05] tracking-tight max-w-md">
            Your organization is a{" "}
            <motion.span key={wordIdx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="text-[#6161FF] inline-block">{WORDS[wordIdx]}</motion.span>{" "}
            structure.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="mt-6 text-lg text-[#4b4e63] max-w-sm font-medium">
            See it. Shape it. Share it. Org charts that build themselves from the data you already own.
          </motion.p>
        </div>
        <div className="flex-1 px-16 pb-8 max-h-[52vh]"><BrandTree /></div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {step === "email" ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-3xl font-black tracking-tight">Sign in to your workspace</h2>
              <p className="mt-2 text-[#676a82] font-medium">No passwords here — we'll email you a one-time code.</p>
              <form onSubmit={(e) => { e.preventDefault(); if (email.includes("@")) requestOtp(); }} className="mt-10">
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#676a82]">Work email</label>
                <input data-testid="login-email-input" type="email" autoFocus value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  placeholder="you@company.com"
                  className="mt-2 w-full h-14 px-5 rounded-xl border-2 border-[#181B34]/10 text-lg font-semibold focus:outline-none focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/15" />
                <button data-testid="login-continue-btn" type="submit" disabled={busy || !email.includes("@")}
                  className="mt-5 w-full h-14 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99]">
                  {busy ? <RefreshCw size={18} className="animate-spin" /> : <>Continue <ArrowRight size={18} /></>}
                </button>
              </form>
              <div className="mt-10 flex items-start gap-3 p-4 rounded-xl bg-[#F0F3FF]">
                <ShieldCheck size={18} className="text-[#00C875] mt-0.5 shrink-0" />
                <p className="text-sm text-[#4b4e63] font-medium">
                  Passwordless by design. A 6-digit code, valid for 10 minutes, is all you need.
                </p>
              </div>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-[#676a82]">
                New organization?
                <button data-testid="goto-signup-btn" onClick={() => setStep("signup")} className="font-black text-[#6161FF] hover:underline">
                  Create your workspace →
                </button>
              </div>
            </motion.div>
          ) : step === "signup" ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-[#00C875]/12 flex items-center justify-center"><Building2 size={17} className="text-[#00925a]" /></span>
                <h2 className="text-3xl font-black tracking-tight">Create your workspace</h2>
              </div>
              <p className="mt-2 text-[#676a82] font-medium">Your own Organa — invite your team, map every department.</p>
              <form onSubmit={(e) => { e.preventDefault(); if (orgName.trim().length > 1 && fullName.trim() && email.includes("@")) signup(); }} className="mt-8 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#676a82]">Organization name</label>
                  <input data-testid="signup-org-input" autoFocus value={orgName} onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Corporation"
                    className="mt-2 w-full h-13 px-5 py-3.5 rounded-xl border-2 border-[#181B34]/10 text-lg font-semibold focus:outline-none focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/15" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#676a82]">Your name</label>
                  <input data-testid="signup-name-input" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Doe"
                    className="mt-2 w-full h-13 px-5 py-3.5 rounded-xl border-2 border-[#181B34]/10 text-lg font-semibold focus:outline-none focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/15" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#676a82]">Work email</label>
                  <input data-testid="signup-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())}
                    placeholder="you@company.com"
                    className="mt-2 w-full h-13 px-5 py-3.5 rounded-xl border-2 border-[#181B34]/10 text-lg font-semibold focus:outline-none focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/15" />
                </div>
                <button data-testid="signup-submit-btn" type="submit" disabled={busy || orgName.trim().length < 2 || !fullName.trim() || !email.includes("@")}
                  className="w-full h-14 rounded-xl bg-[#00C875] hover:bg-[#00b167] text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99]">
                  {busy ? <RefreshCw size={18} className="animate-spin" /> : <><Sparkles size={18} /> Create workspace</>}
                </button>
              </form>
              <p className="mt-4 text-xs text-[#9699AD] font-medium">You'll become this workspace's Super Admin. 30-day trial, no card needed.</p>
              <button data-testid="back-to-signin-btn" onClick={() => setStep("email")} className="mt-6 text-sm font-bold text-[#676a82] hover:text-[#181B34]">
                ← Back to sign in
              </button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-3xl font-black tracking-tight">Check your inbox</h2>
              <p className="mt-2 text-[#676a82] font-medium">
                We sent a 6-digit code to <span className="font-bold text-[#181B34]">{email}</span>
              </p>
              {devCode && (
                <div data-testid="dev-otp-banner" className="mt-4 p-3 rounded-xl bg-[#FDAB3D]/15 border border-[#FDAB3D]/40 text-sm font-semibold text-[#946011]">
                  Dev mode — email delivery not configured. Your code: <span className="font-mono text-base tracking-widest">{devCode}</span>
                </div>
              )}
              <div className="mt-8 flex gap-2.5 justify-between" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input key={i} ref={(el) => (refs.current[i] = el)} data-testid={`otp-digit-${i}`}
                    inputMode="numeric" value={d}
                    onChange={(e) => onDigit(i, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus(); }}
                    className="w-14 h-16 rounded-xl border-2 border-[#181B34]/12 text-center text-3xl font-black focus:outline-none focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/15" />
                ))}
              </div>
              <label className="mt-6 flex items-center gap-2.5 cursor-pointer select-none">
                <input data-testid="trust-device-checkbox" type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)}
                  className="w-4 h-4 accent-[#6161FF]" />
                <span className="text-sm font-semibold text-[#4b4e63]">Trust this device for 30 days</span>
              </label>
              <div className="mt-8 flex items-center justify-between">
                <button data-testid="back-to-email-btn" onClick={() => setStep("email")} className="text-sm font-bold text-[#676a82] hover:text-[#181B34]">
                  ← Different email
                </button>
                <button data-testid="resend-otp-btn" disabled={cooldown > 0 || busy} onClick={() => requestOtp(true)}
                  className="text-sm font-bold text-[#6161FF] disabled:text-[#9699AD]">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
